/**
 * StockEntryTypePickerWidget — Visual card picker for Stock Entry Type.
 *
 * Shows ERPNext Stock Entry Types as tappable icon cards
 * instead of a raw dropdown. Uses STOCK_ACTIONS config as
 * single source of truth for types, icons, and warehouse modes.
 *
 * Follows TemplateInfoWidget pattern (Plan 126).
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import {
  STOCK_ACTIONS,
  type StockActionConfig,
} from "@/features/common/doc-dashboard/stockActionsConfig";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { getUITranslation } from "@/lib/views/labels/translations";

const i18n: Record<string, { en: string; th: string }> = {
  pick_type: { en: "What do you want to do?", th: "คุณต้องการทำอะไร?" },
  material_receipt: { en: "Receive Stock", th: "รับสินค้าเข้าคลัง" },
  material_receipt_desc: { en: "Add items to warehouse", th: "เพิ่มสินค้าเข้าคลังสินค้า" },
  material_issue: { en: "Issue Stock", th: "เบิกสินค้าออก" },
  material_issue_desc: { en: "Remove items from warehouse", th: "นำสินค้าออกจากคลัง" },
  material_transfer: { en: "Transfer Stock", th: "ย้ายสต็อก" },
  material_transfer_desc: { en: "Move between warehouses", th: "ย้ายระหว่างคลังสินค้า" },
  manufacturing_entry: { en: "Manufacture", th: "ผลิต/ประกอบ" },
  manufacturing_entry_desc: { en: "Record production output", th: "บันทึกผลผลิต" },
  repack_entry: { en: "Repack", th: "แพ็คใหม่" },
  repack_entry_desc: { en: "Repackage items", th: "บรรจุสินค้าใหม่" },
};

/** Only show stock_entry_quick_action types (not opening stock / reconciliation) */
function getStockEntryActions(): StockActionConfig[] {
  return STOCK_ACTIONS.filter((a) => a.kind === "stock_entry_quick_action");
}

export function StockEntryTypePickerWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    if (entry) return language === "th" ? entry.th : entry.en;
    return getUITranslation(key, language) || key;
  };

  const actions = useMemo(() => getStockEntryActions(), []);
  const selectedType = (formData.stock_entry_type as string) || "";

  const handleSelect = (action: StockActionConfig) => {
    if (disabled || isSaving) return;
    if (!action.stockEntryType) return;

    onFieldChange("stock_entry_type", action.stockEntryType);

    // Set warehouse defaults based on type's warehouse mode
    // Clear warehouses that aren't relevant for this type
    if (action.warehouseMode === "to") {
      onFieldChange("from_warehouse", "");
    } else if (action.warehouseMode === "from") {
      onFieldChange("to_warehouse", "");
    }
  };

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation("Stock Entry Type", language)}
        subtitle={t("pick_type")}
      />
      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {actions.map((action) => {
            const isSelected = selectedType === action.stockEntryType;
            const Icon = action.icon;
            const label = t(action.id) || action.label;
            const desc = t(`${action.id}_desc`) || action.description;

            return (
              <button
                key={action.id}
                type="button"
                onClick={() => handleSelect(action)}
                disabled={disabled || isSaving}
                className={cn(
                  "relative flex flex-col items-center gap-2.5 rounded-xl p-5 text-center",
                  "border-2 transition-all duration-200",
                  "hover:bg-accent/50 active:scale-[0.98]",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border/40 bg-background"
                )}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div
                  className={cn(
                    "flex items-center justify-center h-11 w-11 rounded-xl",
                    isSelected
                      ? "bg-primary text-white"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">
                    {label}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
                    {desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
